import { isNull, isUndefined } from 'lodash/fp';
import { toast } from 'react-toastify';
import { all, call, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import download from 'downloadjs';
import { Socket } from 'socket.io-client';

import { fetchAllClients, updateClient, createClient, deleteClient, fetchAvailableClientFeaturePermissions, uploadTermsAndConditionsDocuments, downloadTermsAndConditionsDocument, downloadAllClientData } from '../../../../services/client';
import { resetPassword } from '../../../../services/users';
import { deleteClientFailed, deleteClientStarted, deleteClientSuccessful, downloadClientData, downloadDocumentFailed, downloadDocumentStarted, downloadDocumentSuccessful, exportClientDataFailed, exportClientDataSuccessful, fetchAllClientsFailed, fetchAllClientsSuccessful, fetchClientFeaturePermissionsFailed, fetchClientFeaturePermissionsSuccessful, fetchRejectedQuestionsFailed, fetchRejectedQuestionsSuccessful, forgiveRejectedQuestionsFailed, forgiveRejectedQuestionsStarted, forgiveRejectedQuestionsSuccessful, resetClientUserPassword, setClientSelectedFewShotAgreementTypeId, updateClientFailed, updateClientSuccessful, uploadDocumentFailed, uploadDocumentSuccessful } from './actions';
import { getFilesToUpload, getSelectedClient, getSelectedClientId } from './selectors';
import { Client, ClientActionTypes, ClientFeaturePermissionDB, ClientTermsAndConditionsDocument, RejectedQuestionsByUser } from './types';
import { dotsToDashes } from '../../../../utils/regex-utils';
import { getRejectedQuestionsForClient, resetRejectedQuestions } from '../../../../services/ai';
import { MimeType } from '../../../constants/MimeType';
import { getSocket } from '../../../auth/login/store/selectors';
import { DocumentNameFewShotPriorityOrder } from '../../ai/few-shot/store/types';
import { getFewShotDocumentPriorityOrder } from '../../../../services/fewShotDocument';
import { updateFewShotPriorityOrder } from '../../ai/few-shot/store/actions';

export function* attemptFetchAllClients() {
    try {
        const clients: Client[] = yield call(fetchAllClients);
        yield put(fetchAllClientsSuccessful(clients));
    } catch (e) {
        toast.error('Unable to fetch the latest list of clients. Please try again.');
        yield put(fetchAllClientsFailed((e as Error).message));
    }
}

function* fetchAllClientsWatcher() {
    yield takeLeading(ClientActionTypes.FETCH_ALL_CLIENTS_STARTED, attemptFetchAllClients);
}

export function* attemptUpdateClient() {
    try {
        const client: Client = yield select(getSelectedClient);
        const callToMake = isUndefined(client.clientId) ? createClient : updateClient;
        const clients: Client[] = yield call(callToMake, client);
        yield put(updateClientSuccessful(clients));
        toast(`${client.clientName} successfully ${client.clientId ? 'updated' : 'saved'}.`);
    } catch (e) {
        toast.error('Unable to save the details for this client. Please try again.');
        yield put(updateClientFailed((e as Error).message));
    }
}

function* updateClientWatcher() {
    yield takeLeading(ClientActionTypes.UPDATE_CLIENT_STARTED, attemptUpdateClient);
}

export function* attemptDeleteClient({ payload }: ReturnType<typeof deleteClientStarted>) {
    try {
        const { client: { clientId, clientName } } = payload;
        const deleteRequest = { clientId: clientId! };
        const allClients: Client[] = yield call(deleteClient, deleteRequest);
        yield put(deleteClientSuccessful(allClients));
        toast(`${clientName} successfully deleted.`);
    } catch (e) {
        toast.error('Unable to delete client. Please try again.');
        yield put(deleteClientFailed((e as Error).message));
    }
}

function* deleteClientWatcher() {
    yield takeLeading(ClientActionTypes.DELETE_CLIENT_STARTED, attemptDeleteClient);
}

export function* attemptFetchAvailableClientFeaturePermissions() {
    try {
        const permissions: ClientFeaturePermissionDB[] = yield call(fetchAvailableClientFeaturePermissions);
        yield put(fetchClientFeaturePermissionsSuccessful(permissions));
    } catch (e) {
        toast.error('Unable to fetch feature permissions.');
        yield put(fetchClientFeaturePermissionsFailed((e as Error).message));
    }
}

function* fetchAvailableClientFeaturePermissionsWatcher() {
    yield takeLeading(ClientActionTypes.FETCH_CLIENT_FEATURE_PERMISSIONS_STARTED, attemptFetchAvailableClientFeaturePermissions);
}

export function* attemptResetClientUserPassword({ payload }: ReturnType<typeof resetClientUserPassword>) {
    try {
        const { username } = payload;
        yield call(resetPassword, { username });
        toast(`Password reset link sent to ${username}.`);
    } catch (e) {
        toast.error('Unable to send password reset link. Please try again.');
    }
}

function* resetClientUserPasswordWatcher() {
    yield takeLeading(ClientActionTypes.RESET_CLIENT_USER_PASSWORD, attemptResetClientUserPassword);
}

export function* attemptUploadClientTermsAndConditionsDocuments() {
    try {
        const client: Client = yield select(getSelectedClient);
        const files: File[] = yield select(getFilesToUpload);
        if (files.length > 0 && !isUndefined(client.clientId)) {
            let request = new FormData();
            files.forEach(file => request.append('files', file));
            request.append('fileInfo', JSON.stringify({ clientId: client.clientId }));
            const documents: ClientTermsAndConditionsDocument[] = yield call(uploadTermsAndConditionsDocuments, request);
            yield put(uploadDocumentSuccessful(documents, client.clientId));
            toast('Successfully uploaded terms and conditions document(s)');
        }
    } catch (e) {
        toast.error('Unable to upload document. Please try again.');
        yield put(uploadDocumentFailed((e as Error).message));
    }
}

function* uploadClientTermsAndConditionsDocumentsWatcher() {
    yield takeLeading(ClientActionTypes.UPLOAD_TERMS_AND_CONDITIONS_DOCUMENT_STARTED, attemptUploadClientTermsAndConditionsDocuments);
}

export function* attemptDownloadClientTermsAndConditionsDocument({ payload }: ReturnType<typeof downloadDocumentStarted>) {
    try {
        const documentId = payload;
        const client: Client = yield select(getSelectedClient);
        const selectedDocument = client.clientTermsAndConditionsDocuments.find(({ clientTermsDocumentId }) => clientTermsDocumentId === documentId);
        if (!isUndefined(client.clientId) && !isUndefined(selectedDocument)) {
            const { description, mimeType } = selectedDocument;
            const document: Blob = yield call(downloadTermsAndConditionsDocument, { clientTermsDocumentId: documentId, clientId: client.clientId });
            const formattedDescription = dotsToDashes(description);
            yield call(download, document, formattedDescription, mimeType);
            yield put(downloadDocumentSuccessful());
        }
    } catch (e) {
        yield put(downloadDocumentFailed((e as Error).message));
    }
}

function* downloadClientTermsAndConditionsDocumentsWatcher() {
    yield takeLeading(ClientActionTypes.DOWNLOAD_TERMS_AND_CONDITIONS_DOCUMENT_STARTED, attemptDownloadClientTermsAndConditionsDocument);
}

export function* attemptExportAllClientData() {
    try {
        const client: Client = yield select(getSelectedClient);
        const socket: Socket | null = yield select(getSocket);
        if (!isUndefined(client.clientId) && !isNull(socket)) {
            yield call(downloadAllClientData, { socketId: socket.id, clientId: client.clientId });
            yield put(exportClientDataSuccessful());
        }
    } catch (e) {
        yield put(exportClientDataFailed((e as Error).message));
    }
}

function* exportAllClientDataWatcher() {
    yield takeLeading(ClientActionTypes.EXPORT_CLIENT_DATA_STARTED, attemptExportAllClientData);
}

export function* attemptDownloadAllClientData({ payload }: ReturnType<typeof downloadClientData>) {
    const { clientName, buffer } = payload;
    const mimeType = MimeType.EXCEL;
    const formattedDescription = dotsToDashes(`${clientName} - Data Export`);
    const byteArray = new Uint8Array(buffer);
    const blobOfData = new Blob([byteArray], { type: mimeType });
    yield call(download, blobOfData, formattedDescription, mimeType);
}

function* downloadAllClientDataWatcher() {
    yield takeLeading(ClientActionTypes.DOWNLOAD_ALL_CLIENT_DATA, attemptDownloadAllClientData);
}

export function* attemptFetchAllRejectedQuestionsForClient() {
    try {
        const { clientId }: Client = yield select(getSelectedClient);
        if (!isUndefined(clientId)) {
            // We don't want to call this if we are adding a new Client as rejectedQuestions won't exist
            const rejectedQuestions: RejectedQuestionsByUser[] = yield call(getRejectedQuestionsForClient, { clientId });
            yield put(fetchRejectedQuestionsSuccessful(rejectedQuestions));
        }
    } catch (e) {
        toast.error('Unable to fetch the latest list of rejected questions. Please try again.');
        yield put(fetchRejectedQuestionsFailed((e as Error).message));
    }
}

function* fetchAllRejectedQuestionsForClientWatcher() {
    yield takeLatest(ClientActionTypes.FETCH_REJECTED_CHAT_BOT_QUESTIONS_STARTED, attemptFetchAllRejectedQuestionsForClient);
}

export function* attemptForgiveRejectedQuestion({ payload }: ReturnType<typeof forgiveRejectedQuestionsStarted>) {
    try {
        const { clientId }: Client = yield select(getSelectedClient);
        const rejectedQuestion: RejectedQuestionsByUser[] = yield call(resetRejectedQuestions, { userQuestions: payload, clientId: clientId! });
        yield put(forgiveRejectedQuestionsSuccessful(rejectedQuestion));
    } catch (e) {
        toast.error('Unable to forgive the rejected questions. Please try again.');
        yield put(forgiveRejectedQuestionsFailed((e as Error).message));
    }
}

function* attemptForgiveRejectedQuestionWatcher() {
    yield takeLeading(ClientActionTypes.FORGIVE_REJECTED_CHAT_BOT_QUESTIONS_STARTED, attemptForgiveRejectedQuestion);
}

export function* attemptFetchFewShotAgreementTypePriorityOrder({ payload }: ReturnType<typeof setClientSelectedFewShotAgreementTypeId>) {
    const agreementTypeId = payload;
    const setInitial = true;
    let priorityOrder: DocumentNameFewShotPriorityOrder = {};
    if (!isNull(agreementTypeId)) {
        const clientId: number | undefined = yield select(getSelectedClientId);
        priorityOrder = yield call(getFewShotDocumentPriorityOrder, { agreementTypeId, clientId });
    }
    yield put(updateFewShotPriorityOrder(priorityOrder, setInitial));
}

function* selectedFewShotAgreementTypeUpdatedWatcher() {
    yield takeLeading(ClientActionTypes.SET_CLIENT_SELECTED_FEW_SHOT_AGREEMENT_TYPE_ID, attemptFetchFewShotAgreementTypePriorityOrder);
}

export function* clientSaga() {
    yield all([
        fork(fetchAllClientsWatcher),
        fork(updateClientWatcher),
        fork(deleteClientWatcher),
        fork(fetchAvailableClientFeaturePermissionsWatcher),
        fork(resetClientUserPasswordWatcher),
        fork(uploadClientTermsAndConditionsDocumentsWatcher),
        fork(downloadClientTermsAndConditionsDocumentsWatcher),
        fork(exportAllClientDataWatcher),
        fork(downloadAllClientDataWatcher),
        fork(fetchAllRejectedQuestionsForClientWatcher),
        fork(attemptForgiveRejectedQuestionWatcher),
        fork(selectedFewShotAgreementTypeUpdatedWatcher)
    ]);
}
